
/**
 * Firestore does not support 'undefined' values. This helper recursively removes them.
 * Also removes NaN/Infinity, raw Date objects, and function values.
 */
export const sanitizeForFirestore = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.filter(v => v !== undefined && v !== null).map(v => sanitizeForFirestore(v));
  } else if (obj instanceof Date) {
    return obj.toISOString(); // Firestore rejects raw Date objects
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== undefined && v !== null && typeof v !== 'function')
        .filter(([_, v]) => typeof v !== 'number' || (isFinite(v) && !isNaN(v))) // Remove NaN/Infinity
        .map(([k, v]) => [k, sanitizeForFirestore(v)])
    );
  }
  // Remove NaN/Infinity at primitive level too
  if (typeof obj === 'number' && (!isFinite(obj) || isNaN(obj))) return 0;
  return obj;
};

// Encoding helpers using TextEncoder/TextDecoder for Unicode safety and standard Base64
export const safeEncode = (data: any) => {
  const json = JSON.stringify(data);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const safeDecode = (str: string) => {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const json = new TextDecoder().decode(bytes);
  return JSON.parse(json);
};

// ─── GUARANTEED USER COLLECTION ─────────────────────────────────────
// Every authenticated user must end up with a users/{uid} doc in Firestore.
// The previous flow had silent-drop paths: a 3s read timeout at sign-in, a
// fire-and-forget create, and offline/error fallbacks all proceeded with an
// in-memory profile and never wrote the doc — and the cold-start recovery
// was skipped once login had set the user. This helper is idempotent and
// retries with backoff for the rest of the session, so one transient
// failure can no longer lose a signup.

const ensuredUids = new Set<string>();
const ENSURE_RETRY_DELAYS = [2000, 5000, 15000, 30000, 60000];

export const ensureUserDoc = async (profile: {
  id: string; name: string; email: string; hasLicense: boolean; credits: number;
  [key: string]: any;
}, attempt = 0): Promise<void> => {
  if (!profile?.id || ensuredUids.has(profile.id)) return;
  try {
    // Lazy imports keep this module dependency-light for non-auth callers
    const { db } = await import('./firebase');
    if (!db) return;
    const { doc, getDoc, setDoc } = await import('firebase/firestore');
    const ref = doc(db, 'users', profile.id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      // Firestore rules require role 'user', a non-empty name, and a valid email
      await setDoc(ref, sanitizeForFirestore({
        ...profile,
        role: profile.role || 'user',
        name: profile.name || profile.email?.split('@')[0] || 'Traveler',
        createdAt: profile.createdAt || Date.now(),
      }));
      console.log(`[UserCollection] Created Firestore profile for ${profile.id}`);
    }
    ensuredUids.add(profile.id);
  } catch (err) {
    if (attempt < ENSURE_RETRY_DELAYS.length) {
      setTimeout(() => { ensureUserDoc(profile, attempt + 1); }, ENSURE_RETRY_DELAYS[attempt]);
    } else {
      console.error('[UserCollection] Could not persist user profile after all retries:', err);
    }
  }
};
